import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import translate from 'core/translate'
import { api } from 'core/api/ConnectionManager'
import {
    createEmptyBroadcastDocument,
    getDocumentFingerprint,
    normalizeBroadcastDocument,
    serializeBroadcastDocument,
} from '../templateEditor.document'
import {
    BroadcastTemplate,
    BroadcastTemplateDocument,
    BroadcastTemplateListItem,
    CanvasBgMedia,
    TemplateElement,
} from '../templateEditor.types'

const LIST_PAGE_LIMIT = 20

type ListResponse = {
    data: BroadcastTemplateListItem[]
    nextPageToken: string | null
}

type Params = {
    templateName: string
    setTemplateName: (name: string) => void
    canvasW: number
    canvasH: number
    canvasBg: string
    canvasBgMedia: CanvasBgMedia | null
    showGrid: boolean
    gridSize: number
    elements: TemplateElement[]
    applyDocument: (document: BroadcastTemplateDocument) => void
}

const toListItem = ({ document: _document, ...listItem }: BroadcastTemplate): BroadcastTemplateListItem => listItem

const upsertTemplate = (list: BroadcastTemplateListItem[], item: BroadcastTemplateListItem) =>
    [item, ...list.filter((template) => template.id !== item.id)].sort((a, b) => b.id - a.id)

const parseDeletedIds = (payload: number[] | number | { id?: number | number[] } | null): number[] => {
    if (Array.isArray(payload)) {
        return payload
    }
    if (typeof payload === 'number') {
        return [payload]
    }
    if (payload && typeof payload === 'object') {
        if (Array.isArray(payload.id)) {
            return payload.id
        }
        if (typeof payload.id === 'number') {
            return [payload.id]
        }
    }
    return []
}

/** Список / save / load / delete шаблонов + dirty / discard. */
export const useTemplateEditorPersistence = ({
    templateName,
    setTemplateName,
    canvasW,
    canvasH,
    canvasBg,
    canvasBgMedia,
    showGrid,
    gridSize,
    elements,
    applyDocument,
}: Params) => {
    const [templateId, setTemplateId] = useState<number | null>(null)
    const [templates, setTemplates] = useState<BroadcastTemplateListItem[]>([])
    const [templatesSearch, setTemplatesSearch] = useState('')
    const [nextPageToken, setNextPageToken] = useState<string | null>(null)
    const [isListLoading, setIsListLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isTemplateLoading, setIsTemplateLoading] = useState(false)
    const [discardModalOpen, setDiscardModalOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [savedFingerprint, setSavedFingerprint] = useState(() =>
        getDocumentFingerprint(translate('templateEditorUntitled'), createEmptyBroadcastDocument())
    )

    const templateIdRef = useRef<number | null>(null)
    const loadingTokenRef = useRef(0)
    const pendingDiscardActionRef = useRef<(() => void) | null>(null)
    const isDirtyRef = useRef(false)

    templateIdRef.current = templateId

    const currentDocument = useMemo(
        () =>
            serializeBroadcastDocument({
                canvasW,
                canvasH,
                canvasBg,
                canvasBgMedia,
                showGrid,
                gridSize,
                elements,
            }),
        [canvasBg, canvasBgMedia, canvasH, canvasW, elements, gridSize, showGrid]
    )

    const isDirty = getDocumentFingerprint(templateName, currentDocument) !== savedFingerprint
    isDirtyRef.current = isDirty

    const markSaved = (name: string, document: BroadcastTemplateDocument, id: number | null) => {
        setSavedFingerprint(getDocumentFingerprint(name, document))
        setTemplateId(id)
    }

    const resetToBlank = useCallback(() => {
        const document = createEmptyBroadcastDocument()
        const name = translate('templateEditorUntitled')
        applyDocument(document)
        setTemplateName(name)
        markSaved(name, document, null)
    }, [applyDocument, setTemplateName])

    const closeDiscardModal = () => {
        pendingDiscardActionRef.current = null
        setDiscardModalOpen(false)
    }

    const runOrConfirmDiscard = (action: () => void) => {
        if (!isDirtyRef.current) {
            action()
            return
        }
        pendingDiscardActionRef.current = action
        setDiscardModalOpen(true)
    }

    const confirmDiscardWithoutSave = () => {
        const action = pendingDiscardActionRef.current
        pendingDiscardActionRef.current = null
        setDiscardModalOpen(false)
        action?.()
    }

    const applyLoadedTemplate = useCallback(
        (template: BroadcastTemplate) => {
            const document = normalizeBroadcastDocument(template.document)
            const name = template.name || translate('templateEditorUntitled')
            applyDocument(document)
            setTemplateName(name)
            markSaved(name, document, template.id)
            setTemplates((prev) => upsertTemplate(prev, toListItem(template)))
        },
        [applyDocument, setTemplateName]
    )

    const loadTemplatesPage = useCallback((token?: string | null, replace = false) => {
        setIsListLoading(true)

        const payload: { limit: number; nextPageToken?: string } = { limit: LIST_PAGE_LIMIT }
        if (token) {
            payload.nextPageToken = token
        }

        return api
            .send<object, ListResponse>('getListOfBroadcastTemplates', payload, { hideLoader: true })
            .then((response) => {
                const page = Array.isArray(response?.data) ? response.data : []
                setTemplates((prev) => {
                    if (replace || !token) {
                        return page
                    }
                    const existingIds = new Set(prev.map((item) => item.id))
                    return [...prev, ...page.filter((item) => !existingIds.has(item.id))]
                })
                setNextPageToken(response?.nextPageToken ?? null)
            })
            .finally(() => setIsListLoading(false))
    }, [])

    const loadMoreTemplates = () => {
        if (!isListLoading && nextPageToken) {
            loadTemplatesPage(nextPageToken)
        }
    }

    const loadTemplateById = useCallback(
        (id: number) => {
            const requestToken = ++loadingTokenRef.current
            setIsTemplateLoading(true)

            return api
                .send<object, BroadcastTemplate>('getBroadcastTemplateById', { id })
                .then((template) => {
                    if (requestToken !== loadingTokenRef.current) {
                        return false
                    }
                    applyLoadedTemplate(template)
                    return true
                })
                .catch(() => false)
                .finally(() => {
                    if (requestToken === loadingTokenRef.current) {
                        setIsTemplateLoading(false)
                    }
                })
        },
        [applyLoadedTemplate]
    )

    const selectTemplate = (id: number) => {
        if (id === templateIdRef.current) {
            return
        }
        runOrConfirmDiscard(() => loadTemplateById(id))
    }

    const createNewTemplate = () => runOrConfirmDiscard(resetToBlank)

    const requestDeleteTemplate = (id: number, name: string) => setDeleteTarget({ id, name })

    const closeDeleteModal = () => {
        if (!isDeleting) {
            setDeleteTarget(null)
        }
    }

    const confirmDeleteTemplate = () => {
        if (!deleteTarget) {
            return
        }

        const { id } = deleteTarget
        setIsDeleting(true)

        return api
            .send<{ id: number }, number[]>('deleteBroadcastTemplates', { id }, { hideLoader: true })
            .then((deleted) => {
                const deletedIds = Array.isArray(deleted) ? deleted : [id]
                setTemplates((prev) => prev.filter((template) => !deletedIds.includes(template.id)))
                if (templateIdRef.current != null && deletedIds.includes(templateIdRef.current)) {
                    resetToBlank()
                }
                setDeleteTarget(null)
            })
            .finally(() => setIsDeleting(false))
    }

    const saveTemplate = useCallback(
        (onDone?: (saved: boolean) => void) => {
            const name = templateName.trim() || translate('templateEditorUntitled')
            const payload: { id?: number; name: string; document: BroadcastTemplateDocument } = {
                name,
                document: currentDocument,
            }
            if (templateId != null) {
                payload.id = templateId
            }

            setIsSaving(true)
            return api
                .send<object, BroadcastTemplate>('saveBroadcastTemplate', payload)
                .then((template) => {
                    applyLoadedTemplate(template)
                    onDone?.(true)
                    return true
                })
                .catch(() => {
                    onDone?.(false)
                    return false
                })
                .finally(() => setIsSaving(false))
        },
        [applyLoadedTemplate, currentDocument, templateId, templateName]
    )

    const confirmDiscardAndSave = () => {
        const action = pendingDiscardActionRef.current
        saveTemplate((saved) => {
            if (!saved) {
                return
            }
            pendingDiscardActionRef.current = null
            setDiscardModalOpen(false)
            action?.()
        })
    }

    const filteredTemplates = useMemo(() => {
        const query = templatesSearch.trim().toLowerCase()
        if (!query) {
            return templates
        }
        return templates.filter((template) => template.name.toLowerCase().includes(query))
    }, [templates, templatesSearch])

    useEffect(() => {
        loadTemplatesPage(null, true).catch(() => undefined)
    }, [loadTemplatesPage])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'broadcastTemplateSaved',
            (template: BroadcastTemplate) => {
                if (template?.id) {
                    setTemplates((prev) => upsertTemplate(prev, toListItem(template)))
                }
            },
            listenersId
        )

        api.addObserver(
            'broadcastTemplatesDeleted',
            (payload: number[] | number | { id?: number | number[] } | null) => {
                const deletedIds = parseDeletedIds(payload)
                if (!deletedIds.length) {
                    return
                }
                setTemplates((prev) => prev.filter((template) => !deletedIds.includes(template.id)))
                if (templateIdRef.current != null && deletedIds.includes(templateIdRef.current)) {
                    resetToBlank()
                }
            },
            listenersId
        )

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [resetToBlank])

    useEffect(() => {
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isDirty) {
                return
            }
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [isDirty])

    return {
        templateId,
        templates: filteredTemplates,
        templatesSearch,
        setTemplatesSearch,
        isListLoading,
        isSaving,
        isTemplateLoading,
        hasMoreTemplates: Boolean(nextPageToken),
        isDirty,
        discardModalOpen,
        closeDiscardModal,
        confirmDiscardWithoutSave,
        confirmDiscardAndSave,
        deleteTarget,
        isDeleting,
        requestDeleteTemplate,
        closeDeleteModal,
        confirmDeleteTemplate,
        loadMoreTemplates,
        selectTemplate,
        createNewTemplate,
        saveTemplate,
    }
}
